/**
 * hrc-quickadd-popup.js
 * v1.0 — Renk & Beden Seçim Popup
 *
 * Akış:
 *  1. hrc-product-card.js, quick-add tıklandığında
 *     window.HrcQuickAddPopup.open(productHandle) çağırır.
 *  2. Popup, Shopify product JSON'unu fetch eder.
 *  3. Varyantları renk / beden / genel olarak ayırır.
 *  4. Kullanıcı seçim yapar → Sepete Ekle aktif olur.
 *  5. AJAX cart/add.js ile sepete ekler.
 */

(function () {
  "use strict";

  /* ─────────────────────────────────────────
     Sabitler & Yardımcılar
  ───────────────────────────────────────── */

  const COLOR_NAMES = ["color", "colour", "renk", "couleur", "farbe"];
  const SIZE_NAMES = [
    "size",
    "beden",
    "taille",
    "größe",
    "grosse",
    "ağırlık",
    "agirlik",
    "weight",
    "ounce",
    "ounces",
    "oz",
  ];
  const LETTER_SIZE_ORDER = [
    "xxs",
    "xs",
    "s",
    "m",
    "l",
    "xl",
    "xxl",
    "2xl",
    "3xl",
    "4xl",
    "5xl",
  ];
  const i18n = window.hrcI18n || {};
  const t = (key, fallback) => i18n[key] || fallback;
  const tCount = (key, count, fallbackTpl) =>
    (i18n[key] || fallbackTpl)
      .replace("__COUNT__", String(count))
      .replace("{{ count }}", String(count));

  function isColorOption(name) {
    return COLOR_NAMES.includes((name || "").toLowerCase().trim());
  }

  function isSizeOption(name) {
    return SIZE_NAMES.includes((name || "").toLowerCase().trim());
  }

  /** 10OZ / 12OZ / S-M-L → küçükten büyüğe */
  function sortOptionValues(values) {
    if (!Array.isArray(values) || values.length < 2) return values || [];

    const sortKey = (raw) => {
      const label = String(raw ?? "").trim();
      const numMatch = label.replace(",", ".").match(/-?\d+(?:\.\d+)?/);
      if (numMatch) {
        const num = Number(numMatch[0]);
        if (Number.isFinite(num)) return { type: 0, key: num, label };
      }
      const letterIdx = LETTER_SIZE_ORDER.indexOf(label.toLowerCase());
      if (letterIdx >= 0) return { type: 1, key: letterIdx, label };
      return { type: 2, key: label.toLowerCase(), label };
    };

    return [...values].sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      if (ka.type !== kb.type) return ka.type - kb.type;
      if (ka.type === 2) {
        return String(ka.key).localeCompare(String(kb.key), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      return ka.key - kb.key;
    });
  }

  function money(cents) {
    if (window.HrcFormatMoney) {
      return window.HrcFormatMoney(cents, { forceDecimals: true });
    }
    if (window.HrcWebDiscount?.formatCents) {
      return window.HrcWebDiscount.formatCents(cents, { forceDecimals: true });
    }
    const abs = Math.round(Math.abs(Number(cents) || 0));
    const grouped = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${grouped},${String(abs % 100).padStart(2, "0")}TL`;
  }

  function toCents(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
  }

  function readInventoryQty(variant) {
    if (Number.isFinite(variant?.inventory_quantity)) {
      return Number(variant.inventory_quantity);
    }
    return NaN;
  }

  /** Stok / policy üzerinden satılabilir mi? */
  function isVariantSellable(variant) {
    if (!variant) return false;

    const tracked =
      variant.inventory_management != null &&
      variant.inventory_management !== "";
    if (!tracked) return true;
    if (variant.inventory_policy === "continue") return true;
    if (variant.available === true) return true;

    const inv = readInventoryQty(variant);
    if (Number.isFinite(inv) && inv > 0) return true;

    if (variant.available === false) return false;
    return true;
  }

  function isComingSoonVariant(variant) {
    return !!variant && toCents(variant.price) === 0;
  }

  function isPurchasableVariant(variant) {
    return isVariantSellable(variant) && !isComingSoonVariant(variant);
  }

  function optionValueState(product, val) {
    const matches = (product.variants || []).filter(
      (v) => v.option1 === val || v.option2 === val || v.option3 === val,
    );
    if (!matches.length) return "unavailable";
    if (matches.some((v) => isPurchasableVariant(v))) return "ok";
    if (matches.every((v) => isComingSoonVariant(v))) return "coming-soon";
    return "unavailable";
  }

  function getRoutesRoot() {
    const root = window.Shopify?.routes?.root || "/";
    return root.endsWith("/") ? root : `${root}/`;
  }

  function getCartAddUrl() {
    return `${getRoutesRoot()}cart/add.js`;
  }

  /** Liquid product.price / product.compare_at_price + web indirimi */
  function getProductPricing(product) {
    let price = toCents(product.price ?? product.price_min);
    let compare = toCents(
      product.compare_at_price ?? product.compare_at_price_min,
    );

    if (
      (!compare || compare <= price) &&
      Array.isArray(product.variants) &&
      product.variants.length
    ) {
      let minPrice = Infinity;
      let minCompare = Infinity;

      product.variants.forEach((v) => {
        const vp = toCents(v.price);
        const vc = toCents(v.compare_at_price);
        if (vp > 0 && vp < minPrice) minPrice = vp;
        if (vc > 0 && vc < minCompare) minCompare = vc;
      });

      if (minPrice !== Infinity) price = minPrice;
      if (minCompare !== Infinity) compare = minCompare;
    }

    if (window.HrcWebDiscount?.resolveDisplayPricing) {
      const resolved = window.HrcWebDiscount.resolveDisplayPricing(price, compare);
      return {
        price: resolved.price,
        compare: resolved.compare,
        webDiscount: resolved.webDiscount,
        percent: resolved.percent,
        label: resolved.label,
      };
    }

    return {
      price,
      compare: price > 0 && compare > price ? compare : 0,
      webDiscount: false,
    };
  }

  function resolveProductPricing(product, hint = {}) {
    // hint.price katalog kuruşudur (kartın data-base-price değeri).
    // Vitrin fiyatı (data-price) buraya gelirse web indirimi ikinci kez uygulanır.
    const hintPrice = toCents(hint.price);
    const hintCompare = toCents(hint.compareAtPrice);

    if (hintPrice > 0) {
      if (window.HrcWebDiscount?.resolveDisplayPricing) {
        return window.HrcWebDiscount.resolveDisplayPricing(
          hintPrice,
          hintCompare,
        );
      }
      return {
        price: hintPrice,
        compare: hintCompare > hintPrice ? hintCompare : 0,
      };
    }

    return getProductPricing(product);
  }

  function syncPopupVariantInput(variant) {
    const productId = state.product?.id;
    if (variantIdInput) {
      variantIdInput.value = variant?.id ? String(variant.id) : "";
      if (productId) variantIdInput.setAttribute("data-product-id", String(productId));
      variantIdInput.dispatchEvent(new Event("input", { bubbles: true }));
      variantIdInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (popup && productId) {
      popup.setAttribute("data-product-id", String(productId));
      if (variant?.id) popup.setAttribute("data-variant-id", String(variant.id));
    }
    const priceRow = priceEl?.closest("[data-hrc-clay-price], .hrc-popup-price-row");
    const catalogCents = toCents(variant?.price);
    if (window.HrcClaySync?.applyCatalog && priceRow && catalogCents) {
      window.HrcClaySync.applyCatalog(priceRow, catalogCents, variant?.id, productId);
    } else if (priceEl && productId) {
      priceEl.setAttribute("data-product-id", String(productId));
      if (variant?.id) priceEl.setAttribute("data-variant-id", String(variant.id));
    }
    window.HrcB2B?.notifyPricingApps?.(popup || overlay, {
      variant,
      variantId: variant?.id,
      productId,
    });
  }

  function applyPriceUI(
    priceCents,
    compareCents,
    { updateBadge = false, webLabel = "" } = {},
  ) {
    const priceRow = priceEl?.closest(".hrc-popup-price-row");
    if (priceRow) priceRow.removeAttribute("hidden");

    if (toCents(priceCents) === 0) {
      priceEl.textContent = t("comingSoon", "Coming Soon");
      priceEl.classList.remove("is-sale");
      priceEl.classList.add("is-coming-soon");
      oldEl?.setAttribute("hidden", "");
      saveEl?.setAttribute("hidden", "");
      if (updateBadge) badge?.setAttribute("hidden", "");
      return;
    }

    if (window.HrcB2B?.skipThemePricePaint?.()) {
      priceEl.classList.remove("is-coming-soon");
      if (!priceEl.textContent.trim()) priceEl.textContent = money(priceCents);
      const row = priceEl.closest("[data-hrc-clay-price]");
      if (window.HrcClaySync?.applyCatalog && row) {
        window.HrcClaySync.applyCatalog(
          row,
          priceCents,
          state.variant?.id || row.getAttribute("data-variant-id"),
          state.product?.id,
        );
      }
      if (updateBadge && badge) {
        badge.setAttribute("data-b2b-badge", "");
      }
      return;
    }

    priceEl.classList.remove("is-coming-soon");
    priceEl.textContent = money(priceCents);

    if (compareCents > priceCents) {
      priceEl.classList.add("is-sale");
      oldEl.textContent = money(compareCents);
      oldEl.removeAttribute("hidden");
      saveEl.textContent = `${money(compareCents - priceCents)} tasarruf`;
      saveEl.removeAttribute("hidden");

      if (updateBadge) {
        if (webLabel) {
          badge.textContent = webLabel;
          badge.removeAttribute("hidden");
        } else {
          const discount = Math.round(
            ((compareCents - priceCents) / compareCents) * 100,
          );
          badge.textContent = `-${discount}%`;
          badge.removeAttribute("hidden");
        }
      }
      return;
    }

    priceEl.classList.remove("is-sale");
    oldEl?.setAttribute("hidden", "");
    saveEl?.setAttribute("hidden", "");
    if (updateBadge) badge?.setAttribute("hidden", "");
  }

  /** Shopify /products/{handle}.js → images genelde string URL dizisidir */
  function normalizeImageItem(item, fallbackAlt) {
    if (typeof item === "string") {
      return { src: item, alt: fallbackAlt };
    }
    if (!item || typeof item !== "object") return null;

    const preview = item.preview_image;
    const src =
      item.src ||
      item.url ||
      (typeof preview === "string" ? preview : preview?.src) ||
      "";

    return {
      src: typeof src === "string" ? src : "",
      alt: item.alt || fallbackAlt,
    };
  }

  function getProductImages(product) {
    const alt = product.title || "";
    const raw = [];

    if (Array.isArray(product.images) && product.images.length) {
      raw.push(...product.images);
    }

    if (Array.isArray(product.media) && product.media.length) {
      product.media.forEach((m) => {
        if (!m.media_type || m.media_type === "image") {
          raw.push(m.preview_image || m);
        }
      });
    }

    if (!raw.length && product.featured_image) {
      raw.push(product.featured_image);
    }

    const seen = new Set();
    const images = [];

    raw.forEach((item) => {
      const normalized = normalizeImageItem(item, alt);
      if (!normalized?.src || seen.has(normalized.src)) return;
      seen.add(normalized.src);
      images.push(normalized);
    });

    return images.slice(0, 6);
  }

  function resolveImageUrl(src, width = 800) {
    if (!src) return "";
    let url = src;
    if (url.startsWith("//")) url = `https:${url}`;

    try {
      const parsed = new URL(url, window.location.origin);
      if (
        parsed.hostname.includes("cdn.shopify.com") ||
        parsed.hostname.includes("shopifycdn.com")
      ) {
        parsed.searchParams.set("width", String(width));
        return parsed.toString();
      }
    } catch {
      /* legacy _800x suffix */
    }

    return url.replace(/(\.(jpg|jpeg|png|gif|webp))(\?.*)?$/i, `_800x$1$3`);
  }

  /* ─────────────────────────────────────────
     DOM Referansları
  ───────────────────────────────────────── */

  const overlay = document.getElementById("hrc-popup-overlay");
  const popup = document.getElementById("hrc-popup");
  const closeBtn = document.getElementById("hrc-popup-close");
  const imgTrack = document.getElementById("hrc-popup-track");
  const dotsEl = document.getElementById("hrc-popup-dots");
  const navPrev = document.getElementById("hrc-popup-prev");
  const navNext = document.getElementById("hrc-popup-next");
  const badge = document.getElementById("hrc-popup-badge");
  const brandEl = document.getElementById("hrc-popup-brand");
  const labelEl = document.getElementById("hrc-popup-label");
  const titleEl = document.getElementById("hrc-popup-title");
  const priceEl = document.getElementById("hrc-popup-price");
  const oldEl = document.getElementById("hrc-popup-old");
  const saveEl = document.getElementById("hrc-popup-save");
  const variantIdInput = document.getElementById("hrc-popup-variant-id");
  const colorGroup = document.getElementById("hrc-popup-color-group");
  const colorLabel = document.getElementById("hrc-popup-color-label");
  const colorRow = document.getElementById("hrc-popup-color-row");
  const sizeGroup = document.getElementById("hrc-popup-size-group");
  const sizeRow = document.getElementById("hrc-popup-size-row");
  const sizeGuide = document.getElementById("hrc-popup-size-guide");
  const stockEl = document.getElementById("hrc-popup-stock");
  const stockText = document.getElementById("hrc-popup-stock-text");
  const genericEl = document.getElementById("hrc-popup-generic-group");
  const qtyMinus = document.getElementById("hrc-popup-qty-minus");
  const qtyPlus = document.getElementById("hrc-popup-qty-plus");
  const qtyDisplay = document.getElementById("hrc-popup-qty-display");
  const qtyLabel = document.getElementById("hrc-popup-qty-label");
  const btnAdd = document.getElementById("hrc-popup-btn-add");
  const btnText = document.getElementById("hrc-popup-btn-text");
  const btnDetail = document.getElementById("hrc-popup-btn-detail");

  const MIN_QTY = 1;
  const MAX_QTY = 99;

  if (!overlay) return; // Snippet render edilmemişse dur

  /* ─────────────────────────────────────────
     State
  ───────────────────────────────────────── */

  let state = {
    product: null,
    variants: [],
    options: [],
    selected: {}, // { optionName: value }
    quantity: MIN_QTY,
    imgIndex: 0,
    imgTotal: 0,
    successTimer: null,
  };

  const cartQtyByVariant = new Map();

  async function refreshCartQtyMap() {
    try {
      const res = await fetch(`${getRoutesRoot()}cart.js`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const cart = await res.json();
      cartQtyByVariant.clear();
      (cart?.items || []).forEach((item) => {
        const id = Number(item.variant_id || item.id);
        if (!Number.isFinite(id)) return;
        cartQtyByVariant.set(
          id,
          (cartQtyByVariant.get(id) || 0) + Number(item.quantity || 0),
        );
      });
    } catch (_) {
      /* ignore */
    }
  }

  /* ─────────────────────────────────────────
     Görsel Slider
  ───────────────────────────────────────── */

  function syncSlideUI() {
    document.querySelectorAll(".hrc-popup-dot").forEach((d, i) => {
      d.classList.toggle("is-active", i === state.imgIndex);
    });

    const canLoop = state.imgTotal > 1;
    navPrev.classList.toggle("hrc-popup-nav--disabled", !canLoop);
    navNext.classList.toggle("hrc-popup-nav--disabled", !canLoop);
    navPrev.setAttribute("aria-disabled", canLoop ? "false" : "true");
    navNext.setAttribute("aria-disabled", canLoop ? "false" : "true");
  }

  function normalizeSlideIndex(index) {
    if (state.imgTotal <= 0) return 0;
    if (state.imgTotal === 1) return 0;
    return ((index % state.imgTotal) + state.imgTotal) % state.imgTotal;
  }

  function setSlidePosition(index) {
    state.imgIndex = normalizeSlideIndex(index);
    imgTrack.style.transform = `translateX(-${state.imgIndex * 100}%)`;
    syncSlideUI();
  }

  function buildSlider(images, startIndex = 0) {
    const start =
      images.length <= 1
        ? 0
        : ((startIndex % images.length) + images.length) % images.length;

    imgTrack.innerHTML = "";
    dotsEl.innerHTML = "";
    state.imgTotal = images.length;
    state.imgIndex = start;

    imgTrack.classList.add("is-instant");
    imgTrack.style.transform = `translateX(-${start * 100}%)`;

    images.forEach((img, i) => {
      const slide = document.createElement("div");
      slide.className = "hrc-popup-img-slide";

      if (img?.src) {
        const el = document.createElement("img");
        el.src = resolveImageUrl(img.src, 800);
        el.alt = img.alt || state.product?.title || "";
        el.loading = i === start ? "eager" : "lazy";
        slide.appendChild(el);
      }

      imgTrack.appendChild(slide);

      const dot = document.createElement("button");
      dot.className = "hrc-popup-dot" + (i === start ? " is-active" : "");
      dot.setAttribute("aria-label", t("imageN", "Image __N__").replace("__N__", String(i + 1)).replace("{{ n }}", String(i + 1)));
      dot.addEventListener("click", () => goSlide(i));
      dotsEl.appendChild(dot);
    });

    void imgTrack.offsetHeight;
    imgTrack.classList.remove("is-instant");
    syncSlideUI();

    // Tek görselde nav ve dots gizle
    dotsEl.style.display = images.length <= 1 ? "none" : "";
    navPrev.style.display = images.length <= 1 ? "none" : "";
    navNext.style.display = images.length <= 1 ? "none" : "";
  }

  function goSlide(n) {
    setSlidePosition(n);
  }

  navPrev.addEventListener("click", () => goSlide(state.imgIndex - 1));
  navNext.addEventListener("click", () => goSlide(state.imgIndex + 1));

  // Touch swipe — sol panel
  let touchStartX = 0;
  let touchStartY = 0;

  overlay.addEventListener(
    "touchstart",
    (e) => {
      if (!e.target.closest(".hrc-popup-left")) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    },
    { passive: true },
  );

  overlay.addEventListener(
    "touchmove",
    (e) => {
      if (!e.target.closest(".hrc-popup-left")) return;
      const dx = Math.abs(e.touches[0].clientX - touchStartX);
      const dy = Math.abs(e.touches[0].clientY - touchStartY);
      if (dx > dy && dx > 8) e.preventDefault();
    },
    { passive: false },
  );

  overlay.addEventListener(
    "touchend",
    (e) => {
      if (!e.target.closest(".hrc-popup-left")) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) >= 40)
        goSlide(dx < 0 ? state.imgIndex + 1 : state.imgIndex - 1);
    },
    { passive: true },
  );

  /* ─────────────────────────────────────────
     Varyant Seçim Sistemi
  ───────────────────────────────────────── */

  function buildOptions(product) {
    colorRow.innerHTML = "";
    sizeRow.innerHTML = "";
    genericEl.innerHTML = "";

    state.selected = {};
    state.options = product.options;

    let hasColor = false;
    let hasSize = false;

    product.options.forEach((opt) => {
      const isColor = isColorOption(opt.name);
      const isSize = isSizeOption(opt.name);

      if (isColor) {
        hasColor = true;
        colorGroup.removeAttribute("hidden");
        colorLabel.textContent = "";

        opt.values.forEach((val) => {
          const btn = document.createElement("button");
          btn.className = "hrc-popup-swatch";
          btn.setAttribute("aria-label", val);
          btn.setAttribute("title", val);
          btn.dataset.option = opt.name;
          btn.dataset.value = val;

          // Renk değerini hex'e çevirmeye çalış
          const hex = colorNameToHex(val);
          if (hex) {
            btn.style.background = hex;
            if (isLightColor(hex)) {
              btn.style.border = "1.5px solid rgba(0,0,0,0.15)";
            }
          } else {
            // Hex bulunamazsa ilk harf
            btn.style.background = stringToColor(val);
            btn.textContent = val.charAt(0).toUpperCase();
            btn.style.fontSize = "10px";
            btn.style.fontWeight = "500";
            btn.style.color = "#fff";
          }

          const colorState = optionValueState(product, val);

          if (colorState !== "ok") {
            btn.classList.add(
              colorState === "coming-soon" ? "is-coming-soon" : "is-soldout",
            );
            btn.disabled = true;
            btn.title =
              colorState === "coming-soon"
                ? t("comingSoon", "Coming Soon")
                : val;
          } else {
            btn.addEventListener("click", () =>
              selectOption(opt.name, val, btn, ".hrc-popup-swatch"),
            );
          }
          colorRow.appendChild(btn);
        });
      } else if (isSize) {
        hasSize = true;
        sizeGroup.removeAttribute("hidden");

        sortOptionValues(opt.values).forEach((val) => {
          const btn = document.createElement("button");
          btn.className = "hrc-popup-size-btn";
          btn.textContent = val;
          btn.dataset.option = opt.name;
          btn.dataset.value = val;

          // Stok kontrolü
          const sizeState = optionValueState(product, val);

          if (sizeState !== "ok") {
            btn.classList.add(
              sizeState === "coming-soon" ? "is-coming-soon" : "is-soldout",
            );
            btn.disabled = true;
            btn.title =
              sizeState === "coming-soon"
                ? t("comingSoon", "Coming Soon")
                : t("unavailable", "Stokta yok");
          } else {
            btn.addEventListener("click", () =>
              selectOption(opt.name, val, btn, ".hrc-popup-size-btn"),
            );
          }

          sizeRow.appendChild(btn);
        });
      } else {
        // Genel opsiyon
        const wrap = document.createElement("div");
        wrap.className = "hrc-popup-opt hrc-popup-generic-opt";

        const header = document.createElement("div");
        header.className = "hrc-popup-opt-header";
        header.innerHTML = `
          <span class="hrc-popup-opt-label">${opt.name}</span>
          <span class="hrc-popup-opt-selected" id="hrc-generic-sel-${opt.position}"></span>
        `;

        const row = document.createElement("div");
        row.className = "hrc-popup-generic-row";

        sortOptionValues(opt.values).forEach((val) => {
          const btn = document.createElement("button");
          btn.className = "hrc-popup-generic-btn";
          btn.textContent = val;
          btn.dataset.option = opt.name;
          btn.dataset.value = val;

          const genericState = optionValueState(product, val);

          if (genericState !== "ok") {
            btn.classList.add(
              genericState === "coming-soon" ? "is-coming-soon" : "is-soldout",
            );
            btn.disabled = true;
            btn.title =
              genericState === "coming-soon"
                ? t("comingSoon", "Coming Soon")
                : t("unavailable", "Stokta yok");
          } else {
            btn.addEventListener("click", () => {
              selectOption(opt.name, val, btn, ".hrc-popup-generic-btn");
              const selEl = document.getElementById(
                `hrc-generic-sel-${opt.position}`,
              );
              if (selEl) selEl.textContent = val;
            });
          }
          row.appendChild(btn);
        });

        wrap.appendChild(header);
        wrap.appendChild(row);
        genericEl.appendChild(wrap);
      }
    });

    // Tek varyantlı ürün — otomatik seç
    if (product.variants.length === 1) {
      const v = product.variants[0];
      product.options.forEach((opt, i) => {
        const pos = opt.position ?? i + 1;
        const val = v[`option${pos}`];
        if (val) state.selected[opt.name] = val;
      });
      updateAddButton();
    }
  }

  function selectOption(optName, val, clickedBtn, selector) {
    // Aynı grup içinde aktif sınıfı sıfırla
    clickedBtn
      .closest(
        ".hrc-popup-color-row, .hrc-popup-size-row, .hrc-popup-generic-row",
      )
      ?.querySelectorAll(selector)
      .forEach((b) => b.classList.remove("is-active"));

    clickedBtn.classList.add("is-active");
    state.selected[optName] = val;

    // Renk etiketi güncelle
    if (isColorOption(optName)) colorLabel.textContent = val;

    updateAddButton();
    updateStockNote();
  }

  function getMatchingVariant() {
    return (
      state.variants.find((v) =>
        state.options.every((opt) => {
          const sel = state.selected[opt.name];
          if (!sel) return true;
          const pos = opt.position ?? state.options.indexOf(opt) + 1;
          return v[`option${pos}`] === sel;
        }),
      ) || null
    );
  }

  function getMaxQuantity(variant) {
    if (!variant) return MAX_QTY;
    if (isComingSoonVariant(variant)) return 0;
    if (!isVariantSellable(variant)) return 0;

    const tracked = variant.inventory_management === "shopify";
    const deny = variant.inventory_policy === "deny";
    const inv = readInventoryQty(variant);

    // available=true ama qty 0/eksik → kilitleme (Shopify cart doğrular)
    if (tracked && deny && Number.isFinite(inv) && inv > 0) {
      const inCart = cartQtyByVariant.get(Number(variant.id)) || 0;
      return Math.max(0, Math.min(MAX_QTY, inv - inCart));
    }

    return MAX_QTY;
  }

  function clampQuantity(qty, variant) {
    const max = getMaxQuantity(variant);
    const num = Number.isFinite(qty) ? Math.floor(qty) : MIN_QTY;
    if (max <= 0) return MIN_QTY;
    return Math.min(max, Math.max(MIN_QTY, num));
  }

  function formatQtyLabel(qty) {
    return `${qty} adet`;
  }

  function updateQuantityUI(variant) {
    if (!qtyDisplay) return;

    const allSelected = state.options.every((opt) => state.selected[opt.name]);
    const resolvedVariant =
      variant ?? (allSelected ? getMatchingVariant() : null);
    const qty = state.quantity;

    qtyDisplay.textContent = String(qty);
    if (qtyLabel) qtyLabel.textContent = formatQtyLabel(qty);

    const canChange = allSelected && isPurchasableVariant(resolvedVariant);
    const max = getMaxQuantity(resolvedVariant);

    if (qtyMinus) qtyMinus.disabled = !canChange || qty <= MIN_QTY;
    if (qtyPlus) qtyPlus.disabled = !canChange || qty >= max;
  }

  function setQuantity(nextQty, variant) {
    const allSelected = state.options.every((opt) => state.selected[opt.name]);
    if (!allSelected) return;

    const v = variant ?? getMatchingVariant();
    state.quantity = clampQuantity(nextQty, v);
    updateQuantityUI(v);
  }

  function resetQuantity() {
    state.quantity = MIN_QTY;
    updateQuantityUI(null);
  }

  function updateAddButton() {
    const allSelected = state.options.every((opt) => state.selected[opt.name]);
    const variant = allSelected ? getMatchingVariant() : null;

    if (!allSelected) {
      btnAdd.disabled = true;
      btnText.textContent = t("makeSelection", "Make a selection");
      updateQuantityUI(null);
      return;
    }

    if (!variant) {
      btnAdd.disabled = true;
      btnText.textContent = t("soldOut", "Sold Out");
      updateQuantityUI(variant);
      return;
    }

    if (isComingSoonVariant(variant)) {
      btnAdd.disabled = true;
      btnText.textContent = t("comingSoon", "Coming Soon");
      applyPriceUI(0, 0, { updateBadge: true });
      updateQuantityUI(variant);
      return;
    }

    if (!isVariantSellable(variant)) {
      btnAdd.disabled = true;
      btnText.textContent = t("soldOut", "Sold Out");
      updateQuantityUI(variant);
      return;
    }

    const price = toCents(variant.price);
    const compare = toCents(variant.compare_at_price);
    const resolved = window.HrcWebDiscount?.resolveDisplayPricing
      ? window.HrcWebDiscount.resolveDisplayPricing(price, compare)
      : {
          price,
          compare: compare > price ? compare : 0,
          webDiscount: false,
          label: "",
        };
    applyPriceUI(resolved.price, resolved.compare, {
      updateBadge: true,
      webLabel: resolved.webDiscount || resolved.b2bDiscount ? resolved.label : "",
    });
    syncPopupVariantInput(variant);

    state.quantity = clampQuantity(state.quantity, variant);
    const max = getMaxQuantity(variant);

    if (max <= 0) {
      btnAdd.disabled = true;
      btnText.textContent = t("insufficientStock", "Stok yetersiz");
      updateQuantityUI(variant);
      return;
    }

    btnAdd.disabled = false;
    btnAdd.dataset.variantId = variant.id;
    btnText.textContent = t("addToCart", "Add to Cart");
    btnAdd.classList.remove("is-success");
    updateQuantityUI(variant);
  }

  function updateStockNote() {
    const variant = getMatchingVariant();
    if (!variant) {
      stockEl.setAttribute("hidden", "");
      return;
    }

    stockEl.removeAttribute("hidden");

    if (isComingSoonVariant(variant)) {
      stockEl.className = "hrc-popup-stock is-coming-soon";
      stockText.textContent = t("comingSoon", "Coming Soon");
      return;
    }

    if (!isVariantSellable(variant)) {
      stockEl.className = "hrc-popup-stock is-warn";
      stockText.textContent = t("comboSoldOut", "This combination is sold out");
      return;
    }

    // inventory_quantity varsa ve düşükse göster (0 hariç)
    const inv = readInventoryQty(variant);
    if (Number.isFinite(inv) && inv > 0 && inv <= 10) {
      stockEl.className = "hrc-popup-stock is-warn";
      stockText.textContent = tCount(
        "lowStock",
        inv,
        "Only __COUNT__ left",
      );
    } else {
      stockEl.className = "hrc-popup-stock is-ok";
      stockText.textContent = t("inStock", "Stokta mevcut");
    }
  }

  /* ─────────────────────────────────────────
     Adet Seçici
  ───────────────────────────────────────── */

  qtyMinus?.addEventListener("click", () => {
    setQuantity(state.quantity - 1);
  });

  qtyPlus?.addEventListener("click", () => {
    setQuantity(state.quantity + 1);
  });

  /* ─────────────────────────────────────────
     Sepete Ekle
  ───────────────────────────────────────── */

  btnAdd.addEventListener("click", async () => {
    if (btnAdd.disabled || btnAdd.classList.contains("is-loading")) return;

    const variant = getMatchingVariant();
    if (!variant?.id || !isPurchasableVariant(variant)) return;

    if (
      typeof window.HrcCartGuard?.isZeroPriceVariant === "function" &&
      window.HrcCartGuard.isZeroPriceVariant(variant.id)
    ) {
      return;
    }

    if (
      window.HrcGift?.isGiftOnlyProduct?.(state.product) ||
      window.HrcGift?.isGiftOnlyProduct?.({
        variant_id: variant.id,
        product_id: state.product?.id,
        handle: state.product?.handle,
        tags: state.product?.tags,
      })
    ) {
      return;
    }

    const quantity = clampQuantity(state.quantity, variant);
    state.quantity = quantity;
    updateQuantityUI(variant);

    clearTimeout(state.successTimer);
    btnAdd.classList.add("is-loading");
    btnAdd.setAttribute("aria-disabled", "true");

    const formData = new FormData();
    formData.append("id", String(variant.id));
    formData.append("quantity", String(quantity));

    try {
      const response = await fetch(getCartAddUrl(), {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
        credentials: "same-origin",
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.description || data.message || t("addFailed", "Could not add to cart."),
        );
      }

      if (!data?.id && !data?.variant_id) {
        throw new Error(t("addFailed", "Could not add to cart."));
      }

      if (window.HrcCartGuard?.isUnauthorizedGiftItem?.(data)) {
        throw new Error(
          t("giftOnlyCannotAdd", "This product can only be given as a campaign gift."),
        );
      }

      // Başarı
      btnAdd.classList.remove("is-loading");
      btnAdd.classList.add("is-success");
      btnText.textContent = t("addedToCart", "Added to Cart!");

      // Cart icon badge — anlık güncelle (popup kapanmadan önce)
      try {
        await window.HrcCartBadge?.sync();
      } catch (syncErr) {
        console.error("[HRC Popup] Cart badge sync failed:", syncErr);
      }

      await refreshCartQtyMap();

      window.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { lineItem: data } }),
      );

      state.successTimer = setTimeout(() => {
        close();
        window.HrcCartNotify?.show(data);
      }, 1400);
    } catch (err) {
      btnAdd.classList.remove("is-loading");
      btnText.textContent = "Hata! Tekrar dene";
      console.error("[HRC Popup]", err.message);
      setTimeout(() => {
        btnText.textContent = t("addToCart", "Add to Cart");
      }, 2500);
    } finally {
      btnAdd.removeAttribute("aria-disabled");
    }
  });

  /* ─────────────────────────────────────────
     Açma / Kapama
  ───────────────────────────────────────── */

  async function open(productHandle, options = {}) {
    if (!overlay || !productHandle) return;
    if (
      window.HrcGift?.isGiftOnlyProduct?.({
        handle: productHandle,
      })
    ) {
      return;
    }

    const imageIndex =
      typeof options === "number"
        ? options
        : Number(options?.imageIndex ?? 0) || 0;

    // Reset
    resetUI();
    overlay.removeAttribute("hidden");
    overlay.classList.remove("is-open");
    document.body.style.overflow = "hidden";

    try {
      const product = await fetchProduct(productHandle);
      if (window.HrcGift?.isGiftOnlyProduct?.(product)) {
        close();
        return;
      }
      await refreshCartQtyMap();
      populatePopup(product, imageIndex, options);
      requestAnimationFrame(() => {
        overlay.classList.add("is-open");
        closeBtn.focus();
      });
    } catch (err) {
      console.error("[HRC Popup] Ürün yüklenemedi:", err.message);
      close();
    }
  }

  function close() {
    overlay.classList.remove("is-open");
    document.body.style.overflow = "";
    clearTimeout(state.successTimer);

    const onEnd = () => {
      overlay.setAttribute("hidden", "");
      overlay.removeEventListener("transitionend", onEnd);
    };
    overlay.addEventListener("transitionend", onEnd);
  }

  function resolvePrimaryTag(product, hint) {
    const fromHint = String(hint || "").trim();
    if (fromHint) return fromHint;

    const exclude = String(window.__hrcCardLabelExcludeTags || "")
      .split(",")
      .map((s) => s.trim().toLocaleLowerCase("tr-TR"))
      .filter(Boolean);
    const excludeSet = new Set(exclude);
    const typeL = String(product?.type || product?.product_type || "")
      .trim()
      .toLocaleLowerCase("tr-TR");
    const tags = Array.isArray(product?.tags)
      ? product.tags
      : String(product?.tags || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);

    for (const tag of tags) {
      const clean = String(tag || "").trim();
      if (!clean) continue;
      const tagL = clean.toLocaleLowerCase("tr-TR");
      if (typeL && tagL === typeL) continue;
      if (excludeSet.has(tagL)) continue;
      return clean;
    }
    return "";
  }

  function applyPrimaryLabel(product, hint) {
    if (!labelEl) return;
    const label = resolvePrimaryTag(product, hint);
    if (label) {
      labelEl.textContent = label;
      labelEl.removeAttribute("hidden");
    } else {
      labelEl.textContent = "";
      labelEl.setAttribute("hidden", "");
    }
  }

  function resetUI() {
    state.imgIndex = 0;
    state.imgTotal = 0;
    if (imgTrack) {
      imgTrack.classList.add("is-instant");
      imgTrack.style.transform = "translateX(0)";
      void imgTrack.offsetHeight;
      imgTrack.classList.remove("is-instant");
      imgTrack.innerHTML = "";
    }
    dotsEl.innerHTML = "";
    colorRow.innerHTML = "";
    sizeRow.innerHTML = "";
    genericEl.innerHTML = "";
    titleEl.textContent = "";
    brandEl.textContent = "";
    if (labelEl) {
      labelEl.textContent = "";
      labelEl.setAttribute("hidden", "");
    }
    priceEl.textContent = "";
    priceEl.classList.remove("is-sale");
    colorLabel.textContent = "";
    badge.textContent = "";
    badge.setAttribute("hidden", "");
    oldEl.setAttribute("hidden", "");
    saveEl.setAttribute("hidden", "");
    stockEl.setAttribute("hidden", "");
    colorGroup.setAttribute("hidden", "");
    sizeGroup.setAttribute("hidden", "");
    btnAdd.disabled = true;
    btnAdd.classList.remove("is-success", "is-loading");
    delete btnAdd.dataset.variantId;
    btnText.textContent = t("makeSelection", "Make a selection");
    state.selected = {};
    resetQuantity();
  }

  function populatePopup(product, imageIndex = 0, pricingHint = {}) {
    state.product = product;
    state.variants = product.variants;

    // Marka → etiket → başlık
    titleEl.textContent = product.title;
    const vendor = product.vendor || "";
    brandEl.textContent = vendor;
    applyPrimaryLabel(product, pricingHint?.primaryTag);

    const resolved = resolveProductPricing(product, pricingHint);
    applyPriceUI(resolved.price, resolved.compare, {
      updateBadge: true,
      webLabel:
        resolved.webDiscount || resolved.b2bDiscount ? resolved.label : "",
    });
    syncPopupVariantInput(product.variants?.[0] || null);

    // Yeni etiketi
    if (
      !badge.textContent &&
      (product.tags || []).some((t) =>
        ["yeni", "new"].includes(t.toLowerCase()),
      )
    ) {
      badge.textContent = "Yeni";
      badge.removeAttribute("hidden");
    }

    // Ürün linki
    btnDetail.href = `/products/${product.handle}`;

    // Görseller
    buildSlider(getProductImages(product), imageIndex);

    // Opsiyonlar
    buildOptions(product);
  }

  /* ─────────────────────────────────────────
     Ürün Fetch
  ───────────────────────────────────────── */

  const cache = {};

  async function fetchProduct(handle) {
    if (cache[handle]) return cache[handle];
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    cache[handle] = data;
    return data;
  }

  /* ─────────────────────────────────────────
     Klavye & Overlay kapatma
  ───────────────────────────────────────── */

  closeBtn.addEventListener("click", close);

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && overlay.classList.contains("is-open")) close();
  });

  // Focus trap
  overlay.addEventListener("keydown", (e) => {
    if (e.key !== "Tab") return;
    const focusable = Array.from(
      overlay.querySelectorAll(
        'button:not([disabled]), a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.closest("[hidden]"));

    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ─────────────────────────────────────────
     Renk Yardımcıları
  ───────────────────────────────────────── */

  const COLOR_MAP = {
    siyah: "#111111",
    black: "#111111",
    beyaz: "#f5f5f0",
    white: "#f5f5f0",
    kırmızı: "#c7392a",
    red: "#c7392a",
    mavi: "#2563a8",
    blue: "#2563a8",
    lacivert: "#1a2e5a",
    navy: "#1a2e5a",
    yeşil: "#3d7a4e",
    green: "#3d7a4e",
    mor: "#6b5ea8",
    purple: "#6b5ea8",
    sarı: "#e8b840",
    yellow: "#e8b840",
    turuncu: "#d9642a",
    orange: "#d9642a",
    pembe: "#d4537e",
    pink: "#d4537e",
    gri: "#888780",
    gray: "#888780",
    grey: "#888780",
    kahve: "#7c5c3a",
    brown: "#7c5c3a",
    krem: "#f2efe9",
    cream: "#f2efe9",
    beige: "#f0e8d9",
    amber: "#c97a2a",
    "orman yeşili": "#2d5a38",
    "forest green": "#2d5a38",
    orman: "#2d5a38",
    "açık mavi": "#6aadcf",
    "light blue": "#6aadcf",
  };

  function colorNameToHex(name) {
    return COLOR_MAP[(name || "").toLowerCase().trim()] || null;
  }

  function isLightColor(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 180;
  }

  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${Math.abs(hash) % 360}, 45%, 45%)`;
  }

  /* ─────────────────────────────────────────
     Global API
  ───────────────────────────────────────── */

  window.HrcQuickAddPopup = { open, close };
})();
